<template>
  <main
    class="preview"
    style="margin-top: 0px"
    :style="{
      '--primary-color': settings.primaryColor,
      '--secondary-color': settings.secondaryColor,
      '--promo-color': settings.promotionColor,
      '--font-heading': headingFont,
      '--font-body': bodyFont,
    }"
  >
    <component
      :is="'style'"
      v-if="scopedCss"
    >
      {{ scopedCss }}
    </component>
    <div class="custom-scrollbar">
      <component
        :is="modules[instance.module]"
        v-for="(instance, index) in instances"
        :key="index"
        v-bind="instanceData(instance)"
      />
      <button
        v-show="scY > 300"
        class="top-button text-white"
        @click="goToTop"
      >
        <IconArrow
          direction="up"
          size="md"
        />
      </button>
    </div>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';

import * as Modules from '@/modules';
import smoothScroll from '@/helpers/smoothScroll';
import isObject from '@/helpers/isObject';
import sanitizeCss from '@/helpers/sanitizeCss';
import IconArrow from '@/components/icons/IconArrow.vue';

export default {
  name: 'Preview',
  components: {
    IconArrow,
  },
  data() {
    return {
      scTimer: 0,
      scY: 0,
      instances: [],
    };
  },
  computed: {
    instanceList() {
      return this.$store.getters.activePage.instanceList;
    },
    modules() {
      return Modules;
    },
    headingFont() {
      const { pageHeadingFont, headingFont } = this.settings;
      const isGlobalFont = !pageHeadingFont || pageHeadingFont === 'global';
      return isGlobalFont ? headingFont : pageHeadingFont;
    },
    bodyFont() {
      const { pageBodyFont, bodyFont } = this.settings;
      const isGlobalFont = !pageBodyFont || pageBodyFont === 'global';
      return isGlobalFont ? bodyFont : pageBodyFont;
    },
    scopedCss() {
      return sanitizeCss(this.settings.css);
    },
    ...mapGetters(['activePage', 'instance', 'settings']),
  },
  async mounted() {
    window.addEventListener('scroll', this.handleScroll);

    document.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = event.currentTarget.getAttribute('href');

        if (href && href.match(/^#.+/)) {
          event.preventDefault();

          smoothScroll(href);
        }
      });
    });

    const { hash } = this.$route;
    if (hash) {
      this.$nextTick(() => {
        smoothScroll(hash);
      });
    }

    this.instances = await Promise.all(
      this.activePage.instanceList.map(async (id) => {
        const instance = this.instance(id);
        const [globalKey, { globalReference } = {}] =
          Object.entries(instance.data).find(
            ([, value]) => isObject(value) && value.globalReference,
          ) || [];

        if (globalKey) {
          let globalData = { [globalKey]: undefined };

          try {
            ({ data: globalData } =
              (await this.$store.dispatch('loadGlobal', {
                id: globalReference,
                endpoint: 'globals',
              })) || {});
          } catch (error) {
            console.warn(error);
          }

          instance.data = {
            ...instance.data,
            ...globalData,
          };
        }

        return instance;
      }),
    );
  },
  methods: {
    instanceData(instance) {
      return {
        ...instance.data,
        instanceId: instance.id,
      };
    },
    goToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    handleScroll() {
      if (this.scTimer) {
        return;
      }

      this.scTimer = setTimeout(() => {
        this.scY = window.scrollY;
        clearTimeout(this.scTimer);
        this.scTimer = 0;
      }, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
.preview {
  font-family: $lb-content-font;

  ::v-deep {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-family: $lb-heading-font;
    }

    .container {
      max-width: 100%;
    }

    a:hover {
      text-decoration: none;
    }
  }

  &,
  *,
  &::v-deep,
  &::v-deep * {
    @include media-breakpoint-down(lg) {
      cursor: url('../assets/mobile-pointer.png'), auto !important;
    }
  }

  .custom-scrollbar {
    overflow: visible;
    height: 100%;
    margin: 0;
  }
}

.top-button {
  background: rgba(34, 34, 34, 0.5);
  font-size: 26px;
  position: fixed;
  right: 20px;
  bottom: 100px;
  width: 50px;
  height: 50px;
  z-index: 99;
  cursor: pointer;

  &:hover {
    background: rgba(34, 34, 34, 0.8);
  }
}
</style>
